import React, { useState, useEffect, useMemo } from "react";
import { Box, Grid, GridItem, Flex, SlideFade, useToast, Spinner, Text } from "@chakra-ui/react";
import { SalesTeamHeader } from "./SalesTeamHeader";
import { AddSalesExecutive } from "./AddSalesExecutive";
import { SalesExecutiveCard, ExecutiveType } from "./SalesExecutiveCard";
import { salesPersonService, SalesPersonBasicInfo, SalesPersonDetails } from "../../services/salesPersonService";
import { metricsService } from "../../services/metricsService";

// Interface for combined sales executive data, compatible with ExecutiveType
interface FetchedExecutive {
  // From API response
  details?: {
    business_id: string;
    createdAt: string;
    email: string;
    full_name: string;
    is_admin: boolean;
    project_ids: string[];
    type: string;
    updatedAt: string;
    user_id: string;
    __v: number;
    _id: string;
  };
  stats?: {
    totalMeetings: number;
    activeClientCount: number;
  };
  
  cardId: number;
  id: string | number;
  name: string;
  email: string;
  sopAdherence: number;
  objectionHandling: number;
  talkToListenRatio: number;
  projects: {
    count: string;
    names: string;
  };
  pendingActions: string;
  totalMeetings: string;
  activeClients: string;
  
  assignedProjectNames?: string[];
  totalMeetingsCount?: number;
  activeClientsCount?: number;
}

interface SalesTeamProps {
  hideProjectFilter?: boolean;
}

export function SalesTeam({ hideProjectFilter = false }: SalesTeamProps) {
  const [showContent, setShowContent] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Changed from 'loading'
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [salesExecutivesData, setSalesExecutivesData] = useState<FetchedExecutive[]>([]);
  const toast = useToast();

  useEffect(() => {
    // Small delay before starting card animations - can be kept or removed
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchSalesTeamData = async () => {
      setIsLoading(true);
      setError(null);
      setSalesExecutivesData([]);
      const userStr = localStorage.getItem("user");
      let user: any = null;
      if (userStr) {
        try {
          user = JSON.parse(userStr);
        } catch (e) {
          console.error("Failed to parse user from localStorage", e);
        }
      }

      try {
        // Check if user has Sales role and is not admin
        const isSalesRole = user && user.roles && user.roles.includes("Sales");
        const userId = user?.user_id;

        let basicInfos: SalesPersonBasicInfo[] = [];
        
        if (isSalesRole && userId) {
          // If user is Sales (not admin), fetch only that user's details
          // We'll create a mock basicInfo from the user data and fetch details
          basicInfos = [{
            id: userId,
            name: user.full_name || "",
            email: user.email || ""
          }];
        } else {
          // If user is Admin or no user found, fetch all sales persons
          basicInfos = await salesPersonService.listSalesPersons({ type: 'real_estate' });
          if (!Array.isArray(basicInfos)) {
            throw new Error("Failed to fetch sales team list or data is not an array.");
          }
        }
        
        const buildExecutive = async (basicInfo: SalesPersonBasicInfo, index: number) => {
         try {
          // Details and metrics are independent requests — fetch them concurrently
          // instead of serially so each card's two round-trips overlap. allSettled
          // keeps the original branching: a details failure skips this card, while a
          // metrics failure falls back to the defaults below.
          const [detailsResult, metricsResult] = await Promise.allSettled([
            salesPersonService.getSalesPersonDetails(basicInfo.id),
            metricsService.getSalespersonMetricsSummary(basicInfo.id),
          ]);

          if (detailsResult.status === 'rejected') {
            throw detailsResult.reason;
          }
          const details = detailsResult.value;

          let sopAdherence
          let objectionHandling
          let talkToListenRatio
          let apiMetricsAvailable = false;

          if (metricsResult.status === 'fulfilled') {
            const salesMetrics = metricsResult.value;

            // Only use API data if we get valid numeric values
            if (salesMetrics.sop_adherence_percentage != null && !isNaN(salesMetrics.sop_adherence_percentage)) {
              sopAdherence = salesMetrics.sop_adherence_percentage;
              apiMetricsAvailable = true;
            }

            if (salesMetrics.objection_handling_score != null && !isNaN(salesMetrics.objection_handling_score)) {
              objectionHandling = salesMetrics.objection_handling_score;
              apiMetricsAvailable = true;
            }

            if (salesMetrics.average_talk_percentage != null && !isNaN(salesMetrics.average_talk_percentage)) {
              talkToListenRatio = salesMetrics.average_talk_percentage;
              apiMetricsAvailable = true;
            }
          } else {
            console.warn(`Failed to fetch metrics for ${basicInfo.name}, using defaults:`, metricsResult.reason);
            sopAdherence = 70 + (index % 25); // Range: 70-94
            objectionHandling = 70 + ((index + 5) % 25); // Range: 70-94
            talkToListenRatio = 45 + (index % 35); // Range: 45-79
          }

          // --- Data Transformation and Mapping to ExecutiveType ---
          // Try to get real pending actions count from details, otherwise use mock
          let pendingActionsCount = 0;
          if (details.pendingActions && typeof details.pendingActions === 'string') {
            const parsed = parseInt(details.pendingActions);
            pendingActionsCount = isNaN(parsed) ? Math.floor(Math.random() * 5) + 1 : parsed;
          } else {
            pendingActionsCount = Math.floor(Math.random() * 5) + 1; // mock fallback
          }

          // Projects transformation - prioritize API data
          let projectsData = { count: "0", names: "" };
          if (details.details?.project_ids && details.details.project_ids.length > 0) {
            projectsData = {
              count: String(details.details.project_ids.length).padStart(2, '0'),
              names: details.details.project_ids.join(', '),
            };
          } else if (details.assignedProjectNames && details.assignedProjectNames.length > 0) {
            // Fallback to legacy structure if available
            projectsData = {
              count: String(details.assignedProjectNames.length).padStart(2, '0'),
              names: details.assignedProjectNames.join(', '),
            };
          } else if (details.projects) { // If API returns the structure SalesTeam expects
             projectsData = details.projects;
          }

          return {
            ...basicInfo,
            ...details,
            // cardId: index,
            cardId:  basicInfo.id,
            id: basicInfo.id,
            name: details.details?.full_name || basicInfo.name,
            email: details.details?.email || basicInfo.email,
            sopAdherence,
            objectionHandling,
            talkToListenRatio,
            projects: projectsData,
            pendingActions: details.pendingActions || String(pendingActionsCount).padStart(2, '0'),
            totalMeetings: details.totalMeetings || String(details.stats?.totalMeetings || details.totalMeetingsCount || 0).padStart(2, '0'),
            activeClients: details.activeClients || String(details.stats?.activeClientCount || details.activeClientsCount || 0).padStart(2, '0'),
            apiMetricsAvailable, // Track whether we got real API data
          } as FetchedExecutive & { apiMetricsAvailable: boolean };
         } catch (cardErr) {
           // A single card failing (e.g. its details request) must not blank the
           // whole page — log and skip it; the rest still stream in.
           console.warn(`Failed to load sales person ${basicInfo.name}:`, cardErr);
           return null;
         }
        };

        // The salesperson list is the only blocking request. Render the page now
        // and let each card's details/metrics stream in as they resolve, instead
        // of waiting for every card before showing anything.
        setIsLoading(false);

        basicInfos.forEach((basicInfo, index) => {
          buildExecutive(basicInfo, index).then((exec) => {
            if (!cancelled && exec) {
              setSalesExecutivesData(prev => [...prev, exec]);
            }
          });
        });

      } catch (err: any) {
        if (cancelled) return;
        const errorMessage = err.message || "An unknown error occurred";
        setError(errorMessage);
        toast({
          title: "Error fetching sales team",
          description: errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchSalesTeamData();
    return () => { cancelled = true; };
  }, [toast]);


  const [selectedProject, setSelectedProject] = useState("All Projects");

  const availableProjects = useMemo(() => {
    const projectsSet = new Set<string>();
    salesExecutivesData.forEach(executive => {
      if (executive.projects && executive.projects.names) {
        const projects = executive.projects.names.split(",").map(proj => proj.trim());
        projects.forEach(project => projectsSet.add(project));
      } else if (executive.assignedProjectNames) { // Fallback if API structure is different
         executive.assignedProjectNames.forEach(project => projectsSet.add(project));
      }
    });
    return Array.from(projectsSet).sort();
  }, [salesExecutivesData]);

  const filteredExecutives = useMemo(() => {
    return salesExecutivesData.filter((executive) => {
      const executiveProjectsNames = executive.projects ? executive.projects.names : (executive.assignedProjectNames || []).join(',');
      const matchesSearch = !searchQuery.trim() ||
        executive.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        executiveProjectsNames.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesProject = selectedProject === "All Projects" ||
        executiveProjectsNames.split(",").map(p => p.trim()).includes(selectedProject);
      return matchesSearch && matchesProject;
    });
  }, [salesExecutivesData, searchQuery, selectedProject]);
  

  const getAnimationDelay = (index: number) => {
    if (index === -1) return 0.1;
    return 0.1 + ((index + 1) * 0.08);
  };

  if (isLoading) {
    return (
      <Flex
        flexDirection="column"
        height="100%"
        justifyContent="center"
        alignItems="center"
        bg="gray.50"
      >
        <Spinner size="xl" />
        <Text mt={4}>Loading sales team data...</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex
        flexDirection="column"
        height="100%"
        justifyContent="center"
        alignItems="center"
        bg="gray.50"
        p={5}
      >
        <Text color="red.500" fontSize="lg">Error: {error}</Text>
        <Text color="gray.600" mt={2}>Could not load sales team data. Please try again later.</Text>
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      height="100%"
      overflow="hidden"
      bg="gray.50"
    >
      <SalesTeamHeader
        onSearch={setSearchQuery}
        onProjectFilter={setSelectedProject}
        availableProjects={availableProjects}
        hideProjectFilter={hideProjectFilter}
      />
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        px={{ base: 4, md: 8 }}
        py={6}
      >
        <Grid
          templateColumns={{ base: "1fr", xl: "repeat(2, 1fr)" }}
          gap={{ base: 4, md: 6, xl: 8 }}
          maxW={{ base: "1200px", xl: "1300px" }}
          mx="auto"
          width="100%"
        >
          <GridItem width="100%" px={2}>
            <SlideFade
              in={showContent}
              offsetY="20px"
              transition={{ enter: { duration: 0.5, delay: getAnimationDelay(-1) } }}
            >
              <AddSalesExecutive />
            </SlideFade>
          </GridItem>
          {filteredExecutives.map((executive, index) => (
            <GridItem key={executive.cardId} width="100%" px={2}> {/* Use cardId for key */}
              <SlideFade
                in={showContent}
                offsetY="20px"
                transition={{ enter: { duration: 0.5, delay: getAnimationDelay(index) } }}
              >
                <SalesExecutiveCard executive={{
                  id: executive.cardId,
                  name: executive.name,
                  email: executive.email,
                  sopAdherence: executive.sopAdherence || 0,
                  objectionHandling: executive.objectionHandling || 0,
                  talkToListenRatio: executive.talkToListenRatio || 0,
                  projects: executive.projects || { count: '0', names: '' },
                  pendingActions: executive.pendingActions || '0',
                  totalMeetings: executive.totalMeetings || '0',
                  activeClients: executive.activeClients || '0',
                }} />
              </SlideFade>
            </GridItem>
          ))}
        </Grid>
      </Box>
    </Flex>
  );
}

export default SalesTeam; 